import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { themeQuartz } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { QrCode, Trash2 } from "lucide-react";
import images from "../../../assets/images";
import AddAssetInventory from "./AddAssetInventory";
import {
  AssetInventory as AssetInventoryType,
  supplierFormData,
} from "../../../types/api";
import {
  useAssetInventoryStore,
  useDepartmentStore,
  usePremisesStore,
} from "../../../store/store";
import { toast } from "react-toastify";
import axiosInstance from "../../../common/axiosInstance";
import QRCodeModal from "../../../components/QRCodeModal/QRCodeModal";
import { useLocationStore } from "../../../store/masterDataStore";
import { useUtilityAssetTypeStore } from "../../../store/store";
import { getAllSuppliers } from "../../../service/api";

const gridTheme = themeQuartz.withParams({
  accentColor: "#93AA9A",
  headerHeight: "200px",
  browserColorScheme: "light",
  headerFontSize: 14,
});
const AssetInventory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [qrCodeData, setQrCodeData] = useState<any>(null);
  const [showQRModal, setShowQRModal] = useState<boolean>(false);
  const [suppliers, setSuppliers] = useState<supplierFormData[]>([]);
  const [selectedData, setSelectedData] = useState<AssetInventoryType | null>(
    null
  );
  const {
    assetInventory,
    fetchAssetInventory,
    createAssetInventory,
    loading: storeLoading,
    deleteAssetInventory,
    updateAssetInventory,
  } = useAssetInventoryStore();
  const { allLocations, fetchAllLocations } = useLocationStore();
  const { utilityAssetTypes, fetchUtilityAssetTypes } =
    useUtilityAssetTypeStore();
  useEffect(() => {
    fetchAllLocations();
    fetchAssetInventory();
    fetchUtilityAssetTypes();
  }, [fetchAssetInventory, fetchUtilityAssetTypes]);

  useEffect(() => {
    if (assetInventory && Array.isArray(assetInventory)) {
      setRowData(assetInventory);
    } else {
      setRowData([]);
    }
  }, [assetInventory]);
  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await getAllSuppliers();
      setSuppliers(res.data.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Create optimized lookup maps for O(1) access instead of O(n) find operations
  const locationMap = useMemo(() => {
    const map = new Map<string, string>();
    allLocations.forEach((location) => {
      if (location._id) {
        map.set(location._id, location.room || "N/A");
      }
    });
    return map;
  }, [allLocations]);

  const assetTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    utilityAssetTypes.forEach((type) => {
      if (type._id) {
        map.set(type._id, type.typeName || "N/A");
      }
    });
    return map;
  }, [utilityAssetTypes]);

  const supplierMap = useMemo(() => {
    const map = new Map<string, string>();
    suppliers.forEach((supplier) => {
      if (supplier._id) {
        map.set(supplier._id, supplier.name || "N/A");
      }
    });
    return map;
  }, [suppliers]);

  // Memoized lookup functions for better performance
  const getLocationName = useCallback(
    (locationId: string) => {
      if (!locationId) return "";
      return locationMap.get(locationId) || "N/A";
    },
    [locationMap]
  );

  const getAssetTypeName = useCallback(
    (typeId: string) => {
      if (!typeId) return "";
      return assetTypeMap.get(typeId) || "N/A";
    },
    [assetTypeMap]
  );

  const getSupplierName = useCallback(
    (supplierId: string) => {
      if (!supplierId) return "";
      return supplierMap.get(supplierId) || "N/A";
    },
    [supplierMap]
  );
  const handleView = useCallback((data: AssetInventoryType) => {
    setSelectedData(data);
    setMode("view");
    setIsModal(true);
  }, []);

  const handleEdit = useCallback((data: AssetInventoryType) => {
    setSelectedData(data);
    setMode("edit");
    setIsModal(true);
  }, []);
  const handleQRCode = useCallback(
    async (
      assetInventoryId: string,
      action: "preview" | "download" = "preview",
      format: "png" | "svg" = "png"
    ) => {
      try {
        setLoading(true);

        if (action === "download") {
          // Download directly
          const response = await axiosInstance.get(`/api/asset-inventory-tracking/qr/${assetInventoryId}/download?format=${format}`, { responseType: "blob" });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `asset-inventory-${assetInventoryId}-qr.${format}`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          toast.success("QR Code downloaded successfully");
        } else {
          // Preview - get QR code data
          const response = await axiosInstance.get(`/api/asset-inventory-tracking/qr/${assetInventoryId}`);
          // Display the QR code in modal
          const foundAsset = rowData.find(
            (a: AssetInventoryType) => a._id === assetInventoryId
          );
          setQrCodeData({
            ...response.data,
            assetName: foundAsset?.name || "Asset Inventory",

            qrData: {
              ...response.data.qrData,
            },
          });
          setShowQRModal(true);
        }
      } catch (error) {
        console.error("Error with QR code:", error);
        toast.error(`Failed to ${action} QR code`);
      } finally {
        setLoading(false);
      }
    },
    [rowData]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "assetId",
        headerName: "Asset ID",
        sortable: false,
        minWidth: 100,
        flex: 0.5,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
      },
      {
        field: "name",
        headerName: "Name",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: { padding: "10px 0 0 5px " },
      },
      {
        field: "type",
        headerName: "Type",
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1,
        minWidth: 20,
        sortable: false,
        cellStyle: { padding: "10px 0 0 0 " },
        filter: false,
        cellRenderer: (params: any) => {
          return getAssetTypeName(params.value || "");
        },
      },
      {
        field: "location",
        headerName: "Location",
        sortable: false,
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0.8,
        filter: false,
        cellStyle: { padding: "10px 0 0 0 " },
        cellRenderer: (params: any) => {
          return getLocationName(params.value || "");
        },
      },
      {
        field: "maintenanceVendor",
        headerName: "Maintenance Vendor",
        minWidth: 20,
        flex: 1,
        filter: false,
        cellStyle: { padding: "10px 0 0 0" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => {
          return getSupplierName(params.value || "");
        },
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 20,
        flex: 0.5,
        filter: false,
        cellStyle: { padding: "10px 0 0 0" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => {
          return (
            <span
              className={` font-bold ${params.value === "Active"
                  ? "text-green-500"
                  : params.value === "Inactive"
                    ? "text-red-500"
                    : "text-gray-500"
                }`}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 0 0 0 " },
        minWidth: 20,
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 1,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <img
              onClick={() => handleView(params.data)}
              className="iconTd cursor-pointer"
              src={images.viewIcon}
            />
            <img
              onClick={() => handleEdit(params.data)}
              className="iconTd cursor-pointer"
              src={images.editIcon}
            />
            <Trash2
              onClick={() => deleteAssetInventory(params.data._id)}
              className="cursor-pointer text-red-500"
            />
            <div className="flex gap-1">
              <button
                onClick={() => handleQRCode(params.data._id, "preview")}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Preview QR Code"
              >
                <QrCode className="w-5 h-5 text-blue-600" />
              </button>
            </div>
          </div>
        ),
        filter: false,
      },
    ],
    [
      getAssetTypeName,
      getLocationName,
      getSupplierName,
      handleView,
      handleEdit,
      deleteAssetInventory,
      handleQRCode,
    ]
  );
  const theme = useMemo(() => {
    return gridTheme;
  }, []);
  const defaultColDef = useMemo(
    () => ({
      wrapText: true,
      headerClass: "font-bold text-md bg-gray-100 p-2",
    }),
    []
  );
  return (
    <>
      <div className="px-2 py-4 mt-14 w-full h-full">
        <div className="bg-white rounded-lg shadow py-6  px-2">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb">
              Asset Inventory & Tracking
            </h1>
            <button
              onClick={() => {
                setSelectedData(null);
                setIsModal(true);
                setMode("create");
              }}
              className="flex btn-primary roundedBt iconWithBt text-uppercase mr-4"
            >
              ADD ASSETS INVENTORY
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div
              className="ag-theme-alpine mt-4 w-full"
              style={{ height: "600px", width: "100%" }}
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={theme}
                headerHeight={65}
                rowHeight={60}
                defaultColDef={defaultColDef}
              />
            </div>
          )}
        </div>
      </div>
      <AddAssetInventory
        isOpen={isModal}
        onClose={() => {
          setIsModal(false);

          setSelectedData(null);
        }}
        onSubmit={(data: AssetInventoryType) => {
          if (mode === "create") {
            createAssetInventory(data);
          } else if (mode === "edit") {
            updateAssetInventory(data._id as string, data);
          }
          setIsModal(false);

          setSelectedData(null);
        }}
        mode={mode}
        initialData={selectedData}
      />

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        qrCodeData={qrCodeData}
        onDownloadPNG={() =>
          handleQRCode(qrCodeData?.qrData?.assetId, "download", "png")
        }
        onDownloadSVG={() =>
          handleQRCode(
            qrCodeData?.qrData?.assetId || qrCodeData?.qrData?._id,
            "download",
            "svg"
          )
        }
      />
    </>
  );
};

export default AssetInventory;
